import React, { useEffect, useRef } from 'react';
import { TESSERACT_VERTICES, TESSERACT_EDGES, prefersReducedMotion } from '../utils/tesseractGeometry';

// 4D Tesseract (Hypercube) with smooth rotation animation
const TesseractLogo = ({ size = 32, color = '#00b4d8', className = '', animate = true }) => {
    const canvasRef = useRef(null);
    const animRef = useRef(null);
    const angleRef = useRef({ xw: 0, yz: 0, xy: 0 });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const scale = window.devicePixelRatio || 1;
        const s = size;

        canvas.width = s * scale;
        canvas.height = s * scale;
        canvas.style.width = s + 'px';
        canvas.style.height = s + 'px';
        ctx.scale(scale, scale);

        // Respect the user's reduced-motion preference (render a static frame)
        const shouldAnimate = animate && !prefersReducedMotion();

        const vertices4D = TESSERACT_VERTICES;
        const edges = TESSERACT_EDGES;

        // 4D rotation matrices (rotate in XW and YZ planes)
        const rotate4D = (v, axw, ayz, axy) => {
            let [x, y, z, w] = v;
            // Rotate in XW plane
            let cos1 = Math.cos(axw), sin1 = Math.sin(axw);
            let nx = x * cos1 - w * sin1;
            let nw = x * sin1 + w * cos1;
            x = nx; w = nw;
            // Rotate in YZ plane
            let cos2 = Math.cos(ayz), sin2 = Math.sin(ayz);
            let ny = y * cos2 - z * sin2;
            let nz = y * sin2 + z * cos2;
            y = ny; z = nz;
            // Rotate in XY plane (slower)
            let cos3 = Math.cos(axy), sin3 = Math.sin(axy);
            nx = x * cos3 - y * sin3;
            ny = x * sin3 + y * cos3;
            x = nx; y = ny;
            return [x, y, z, w];
        };

        // Project 4D → 2D with perspective
        const project = (v) => {
            const [x, y, z, w] = v;
            const dist4D = 4.5;
            const dist3D = 4.5;
            // 4D → 3D perspective
            const scale4 = dist4D / (dist4D - w);
            const x3 = x * scale4;
            const y3 = y * scale4;
            const z3 = z * scale4;
            // 3D → 2D perspective
            const scale3 = dist3D / (dist3D - z3);
            const x2 = x3 * scale3;
            const y2 = y3 * scale3;
            return { x: x2, y: y2, depth: z3 + w, scale: scale4 * scale3 };
        };

        const draw = () => {
            ctx.clearRect(0, 0, s, s);
            const cx = s / 2;
            const cy = s / 2;
            const r = s * 0.16;

            const a = angleRef.current;
            if (shouldAnimate) {
                a.xw += 0.008;
                a.yz += 0.006;
                a.xy += 0.004;
            }

            // Project all vertices
            const projected = vertices4D.map(v => {
                const rotated = rotate4D(v, a.xw, a.yz, a.xy);
                return project(rotated);
            });

            // Draw edges (back to front by average depth)
            const edgesWithDepth = edges.map(([i, j]) => ({
                i, j,
                depth: (projected[i].depth + projected[j].depth) / 2
            }));
            edgesWithDepth.sort((a, b) => a.depth - b.depth);

            edgesWithDepth.forEach(({ i, j, depth }) => {
                const p1 = projected[i];
                const p2 = projected[j];
                const normalizedDepth = (depth + 4) / 8; // 0 to 1
                const alpha = 0.15 + normalizedDepth * 0.5;
                const lineWidth = 0.5 + normalizedDepth * 1.2;

                ctx.beginPath();
                ctx.moveTo(cx + p1.x * r, cy + p1.y * r);
                ctx.lineTo(cx + p2.x * r, cy + p2.y * r);
                ctx.strokeStyle = color;
                ctx.globalAlpha = alpha;
                ctx.lineWidth = lineWidth;
                ctx.stroke();
            });

            // Draw vertices (sorted by depth)
            const verticesWithDepth = projected.map((p, i) => ({ ...p, i }));
            verticesWithDepth.sort((a, b) => a.depth - b.depth);

            verticesWithDepth.forEach(({ x, y, depth, scale: sc }) => {
                const normalizedDepth = (depth + 4) / 8;
                const alpha = 0.3 + normalizedDepth * 0.7;
                const dotR = (0.8 + normalizedDepth * 1.5) * (s / 32);

                // Glow
                ctx.beginPath();
                ctx.arc(cx + x * r, cy + y * r, dotR * 2, 0, Math.PI * 2);
                ctx.fillStyle = color;
                ctx.globalAlpha = alpha * 0.15;
                ctx.fill();

                // Dot
                ctx.beginPath();
                ctx.arc(cx + x * r, cy + y * r, dotR, 0, Math.PI * 2);
                ctx.fillStyle = color;
                ctx.globalAlpha = alpha;
                ctx.fill();
            });

            ctx.globalAlpha = 1;

            if (shouldAnimate) {
                animRef.current = requestAnimationFrame(draw);
            }
        };

        draw();

        return () => {
            if (animRef.current) {
                cancelAnimationFrame(animRef.current);
            }
        };
    }, [size, color, animate]);

    return (
        <canvas
            ref={canvasRef}
            className={className}
            style={{ display: 'block' }}
            role="img"
            aria-label="Tesseractive rotating tesseract logo"
        />
    );
};

export default TesseractLogo;
